import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../landingpage/sections/Navbar/index.jsx";
import Footer from "../home/footer";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import * as eventService from "../../services/eventService";
import styles from "../landingpage/sections/Events/EventsSection.module.css";
import { 
  doctordashboardprofile01, 
  doctordashboardprofile02, 
  doctordashboardprofile3 
} from "../imagepath";

import { toast } from "react-toastify";

const PublicEvents = (props) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [activeShareMenu, setActiveShareMenu] = useState(null);
   const REACT_APP_EVENTS_PUBLIC_URL = process.env.REACT_APP_EVENTS_PUBLIC_URL || "https://events.devworkone.com/public/events";
  const itemsPerPage = 12;
  const navigate = useNavigate();

  useEffect(() => {
    fetchEvents();
    window.scrollTo(0, 0);
    
    // Close share menu on click outside
    const handleClickOutside = () => setActiveShareMenu(null);
    window.addEventListener("click", handleClickOutside);
    return () => window.removeEventListener("click", handleClickOutside);
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await eventService.getPublicEvents();
      if (response && response.success) {
        setEvents(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      console.error("Error fetching public events:", error);
    } finally {
      setLoading(false);
    }
  };

  // Derive unique categories from events
  const categories = useMemo(() => {
    const cats = events
      .map(event => event.category)
      .filter(cat => cat && cat.trim() !== "");
    return ["All", ...new Set(cats)];
  }, [events]);

  // Filter events based on category and search query
  const filteredEvents = useMemo(() => {
    return events.filter(event => {
      const matchesCategory = selectedCategory === "All" || event.category === selectedCategory;
      const matchesSearch = event.title?.toLowerCase().includes(searchQuery.toLowerCase()) || 
                           event.location?.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [events, selectedCategory, searchQuery]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const weekday = date.toLocaleDateString("default", { weekday: "long" });
    const month = date.toLocaleDateString("default", { month: "long" });
    const day = date.getDate();
    const year = date.getFullYear();
    return `${weekday} ${month} ${day} ${year}`;
  };

  const getPriceDisplay = (event) => {
    const tiers = event.ticketTiers;
    if (!tiers || tiers.length === 0) return "Free Entry";
    const minPrice = Math.min(...tiers.map((t) => t.price || 0));
    if (minPrice === 0) return "Free Access";
    return `From ${event.currency || 'KES'} ${minPrice.toLocaleString()}`;
  };

  const handleShare = (e, event, type = 'native') => {
    e.stopPropagation();
    const publicBaseUrl = process.env.REACT_APP_EVENTS_PUBLIC_URL || "https://events.devworkone.com/public/events";
    const url = `${publicBaseUrl}/${event._id || event.id}`;
    const text = `Check out this event: ${event.title}`;

    switch (type) {
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodeURIComponent(text + " " + url)}`, '_blank');
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'copy':
        navigator.clipboard.writeText(url);
        toast.success("Link copied to clipboard!");
        break;
      default:
        if (navigator.share) {
          navigator.share({ title: event.title, text: text, url: url }).catch(console.error);
        } else {
          navigator.clipboard.writeText(url);
          toast.success("Link copied to clipboard!");
        }
    }
    setActiveShareMenu(null);
  };

  const toggleShareMenu = (e, eventId) => {
    e.stopPropagation();
    setActiveShareMenu(activeShareMenu === eventId ? null : eventId);
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentEvents = filteredEvents.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredEvents.length / itemsPerPage);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo(0, 0);
  };

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, searchQuery]);

  return (
    <div className="main-wrapper">
      <Navbar {...props} />

      {/* Breadcrumb */}
      <div className="breadcrumb-bar overflow-visible" style={{
          backgroundColor: 'var(--breadcrumb-bg)',
          transition: 'background-color 0.3s ease',
          padding: '40px 0 40px',
          marginTop: '70px',
      }}>
          <div className="container">
              <div className="row align-items-center inner-banner">
                  <div className="col-md-12 col-12 text-center">
                      <nav aria-label="breadcrumb" className="page-breadcrumb">
                          <ol className="breadcrumb" style={{ justifyContent: 'center' }}>
                              <li className="breadcrumb-item">
                                  <a href="/">
                                      <i className="isax isax-home-15" />
                                  </a>
                              </li>
                              <li className="breadcrumb-item">Events</li>
                              <li className="breadcrumb-item active">Events Grid</li>
                          </ol>
                          <h2 className="breadcrumb-title">All Events</h2>
                      </nav>
                  </div>
              </div>
          </div>
          <div className="breadcrumb-bg">
              <ImageWithBasePath src="assets/img/bg/breadcrumb-icon.png" alt="img" className="breadcrumb-bg-03" />
              <ImageWithBasePath src="assets/img/bg/breadcrumb-icon.png" alt="img" className="breadcrumb-bg-04" />
          </div>
      </div>
      {/* /Breadcrumb */}

      <div className="content premium-content-bg">
        <div className="container">
          
          {/* Filter Section */}
          <div className={styles.filterContainer}>
            <div className={styles.searchWrapper}>
              <i className={`fa-solid fa-magnifying-glass ${styles.searchIcon}`}></i>
              <input 
                type="text" 
                className={styles.searchInput} 
                placeholder="Search events by title or location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            
            <div className={styles.categoryList}>
              {categories.map((cat) => (
                <button
                  key={cat}
                  className={`${styles.categoryBtn} ${selectedCategory === cat ? styles.active : ""}`}
                  onClick={() => setSelectedCategory(cat)}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className={styles.loadingGrid}>
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className={styles.skeletonCard}></div>
              ))}
            </div>
          ) : filteredEvents.length > 0 ? (
            <>
              <div className={styles.eventGrid}>
                {currentEvents.map((event) => {
                  const dateDisplay = formatDate(event.startDate);
                  const priceDisplay = getPriceDisplay(event);

                  return (
                    <div
                      key={event._id || event.id}
                      className={styles.eventCard}
                      onClick={() =>
                        window.open(
                          `${REACT_APP_EVENTS_PUBLIC_URL}/${event._id || event.id}`,
                          "_blank",
                        )
                      }
                    >
                      <div className={styles.imageWrapper}>
                        <img
                          src={
                            event.image ||
                            "https://images.unsplash.com/photo-1501281668745-f7f57925c3b4?auto=format&fit=crop&q=80&w=800"
                          }
                          alt={event.title}
                          className={styles.eventImage}
                        />
                        
                        <div className={styles.badgeContainer}>
                          <div className={styles.categoryBadge}>
                            <span className={styles.dot}></span>
                            {event.category || "Event"}
                          </div>
                        </div>

                        <div className={styles.actionButton}>
                          <button 
                            className={styles.shareButton}
                            onClick={(e) => toggleShareMenu(e, event._id || event.id)}
                          >
                            <i className="fa-solid fa-share-nodes"></i>
                          </button>

                          <div className={`${styles.shareMenu} ${activeShareMenu === (event._id || event.id) ? styles.shareMenuVisible : ""}`}>
                            <button className={styles.shareOption} onClick={(e) => handleShare(e, event, 'copy')}>
                              <i className="fa-regular fa-copy"></i> Copy Link
                            </button>
                            <button className={styles.shareOption} onClick={(e) => handleShare(e, event, 'whatsapp')}>
                              <i className="fa-brands fa-whatsapp" style={{ color: '#25D366' }}></i> WhatsApp
                            </button>
                            <button className={styles.shareOption} onClick={(e) => handleShare(e, event, 'facebook')}>
                              <i className="fa-brands fa-facebook" style={{ color: '#1877F2' }}></i> Facebook
                            </button>
                            <button className={styles.shareOption} onClick={(e) => handleShare(e, event, 'twitter')}>
                              <i className="fa-brands fa-x-twitter"></i> Twitter
                            </button>
                          </div>
                        </div>

                        <div className={styles.imageOverlay}></div>
                      </div>

                      <div className={styles.cardBody}>
                        <div className={styles.titleSection}>
                          <div className={styles.titleRow}>
                            <h3 className={styles.eventTitle}>{event.title}</h3>
                            <div className={styles.arrowIcon}>
                              <i className="fa-solid fa-arrow-up-right"></i>
                            </div>
                          </div>

                          <p className={styles.eventDescription}>
                            {event.description || "Join us for an unforgettable experience filled with excitement and discovery."}
                          </p>

                          <div className={styles.metaRow}>
                            <div className={styles.metaItem}>
                              <i className="fa-regular fa-calendar"></i>
                              <span>{dateDisplay}</span>
                            </div>
                            <div className={styles.metaItem}>
                              <i className="fa-solid fa-location-dot"></i>
                              <span>{event.location || event.virtualPlatform || "Online"}</span>
                            </div>
                          </div>
                        </div>

                        <div className={styles.cardFooter}>
                          <div className={styles.footerLeft}>
                            <div className={styles.registrationFlow}>
                              <span className={styles.regLabel}>Pricing</span>
                              <span className={styles.regValue}>{priceDisplay}</span>
                            </div>
                            
                            <div className={styles.avatars}>
                              <div className={styles.avatar}>
                                <img src={doctordashboardprofile01} alt="" />
                              </div>
                              <div className={styles.avatar}>
                                <img src={doctordashboardprofile02} alt="" />
                              </div>
                              <div className={styles.avatar}>
                                <img src={doctordashboardprofile3} alt="" />
                              </div>
                              <div className={styles.moreAvatars}>
                                +12
                              </div>
                            </div>
                          </div>
                          
                          <button 
                            className={styles.buyTicketBtn}
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(`${REACT_APP_EVENTS_PUBLIC_URL}/${event._id || event.id}`, '_blank');
                            }}
                          >
                            Buy Ticket
                          </button>
                        </div>

                        <div className={styles.progressAccent}></div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination ... */}
              {totalPages > 1 && (
                <div className="pagination-wrapper mt-5 d-flex justify-content-center">
                  <nav>
                    <ul className="pagination premium-pagination">
                      <li
                        className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                      >
                        <button
                          className="page-link"
                          onClick={() => paginate(currentPage - 1)}
                        >
                          <i className="fa-solid fa-chevron-left"></i>
                        </button>
                      </li>
                      {[...Array(totalPages)].map((_, i) => (
                        <li
                          key={i}
                          className={`page-item ${currentPage === i + 1 ? "active" : ""}`}
                        >
                          <button
                            className="page-link"
                            onClick={() => paginate(i + 1)}
                          >
                            {i + 1}
                          </button>
                        </li>
                      ))}
                      <li
                        className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                      >
                        <button
                          className="page-link"
                          onClick={() => paginate(currentPage + 1)}
                        >
                          <i className="fa-solid fa-chevron-right"></i>
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </>
          ) : (
            <div className={styles.emptyState}>
              <i className="fa-solid fa-calendar-xmark"></i>
              <h3>No events match your search criteria</h3>
              <p className="text-muted mt-2">
                Try adjusting your filters or search query to find more events.
              </p>
              <button 
                className="btn btn-primary mt-3"
                style={{ backgroundColor: '#ED5A15', borderColor: '#ED5A15', borderRadius: '10px' }}
                onClick={() => { setSelectedCategory("All"); setSearchQuery(""); }}
              >
                Clear all filters
              </button>
            </div>
          )}
        </div>
      </div>

      <style jsx="true">{`
        .premium-breadcrumb {
          background: #f8fafc;
          padding: 60px 0;
          transition: all 0.3s ease;
        }
        .breadcrumb-title {
          font-weight: 800;
          font-size: 36px;
          color: #1a1a1a;
        }
        .premium-content-bg {
          padding: 40px 0 80px;
          background: #f1f5f9;
          transition: all 0.3s ease;
          min-height: 600px;
        }

        /* DARK MODE OVERRIDES */
        [data-theme="dark"] .premium-breadcrumb {
          background: #0a0a0a !important;
          border-bottom: 1px solid #262626;
        }
        [data-theme="dark"] .breadcrumb-title {
          color: #fafafa !important;
        }
        [data-theme="dark"] .premium-content-bg {
          background: #0a0a0a !important;
        }
        [data-theme="dark"] .page-item.disabled .page-link {
          background-color: #1a1a1a !important;
          border-color: #262626 !important;
          color: #444 !important;
        }
        [data-theme="dark"] .page-link {
          background-color: #1a1a1a !important;
          border-color: #262626 !important;
          color: #fafafa !important;
        }
        [data-theme="dark"] .page-item.active .page-link {
          background-color: #ED5A15 !important;
          border-color: #ED5A15 !important;
          color: #fff !important;
        }
        [data-theme="dark"] .breadcrumb-item a {
          color: #a3a3a3;
        }
        [data-theme="dark"] .breadcrumb-item.active {
          color: #fafafa !important;
        }
      `}</style>

      <Footer {...props} />

      {/* Support Info Section */}
      <div className="support-section py-5" style={{ backgroundColor: 'var(--breadcrumb-bg)', borderTop: '1px solid #eee' }}>
        <div className="container">
          <div className="row text-center">
            <div className="col-md-12 mb-4">
              <h3 style={{ fontWeight: '700' }}>Need Help with Booking?</h3>
              <p className="text-muted">Our customer care team is available to assist you</p>
            </div>
            <div className="col-md-4 mb-3">
              <div className="support-card p-4 rounded shadow-sm bg-white h-100">
                <i className="fa-solid fa-phone mb-3" style={{ fontSize: '24px', color: '#ED5A15' }}></i>
                <h5>Call Us</h5>
                <p className="mb-0"><a href="tel:+263 78 223 2232" style={{ color: 'inherit', textDecoration: 'none' }}>+263 782 232 232</a></p>
              </div>
            </div>
            <div className="col-md-4 mb-3">
              <div className="support-card p-4 rounded shadow-sm bg-white h-100">
                <i className="fa-brands fa-whatsapp mb-3" style={{ fontSize: '24px', color: '#25d366' }}></i>
                <h5>WhatsApp</h5>
                <p className="mb-0"><a href="https://wa.me/263782232232" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>+263 782 232 232</a></p>
              </div>
            </div>
            <div className="col-md-4 mb-3">
              <div className="support-card p-4 rounded shadow-sm bg-white h-100">
                <i className="fa-solid fa-envelope mb-3" style={{ fontSize: '24px', color: '#ED5A15' }}></i>
                <h5>Email Support</h5>
                <p className="mb-0"><a href="mailto:support@howzitstar.com" style={{ color: 'inherit', textDecoration: 'none' }}>support@howzitstar.com</a></p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx="true">{`
        .support-card {
          transition: transform 0.3s ease;
        }
        .support-card:hover {
          transform: translateY(-5px);
        }
        [data-theme="dark"] .support-card {
          background-color: #1a1a1a !important;
          border: 1px solid #262626;
        }
        [data-theme="dark"] .support-section {
          background-color: #0a0a0a !important;
          border-top: 1px solid #262626 !important;
        }
      `}</style>
    </div>
  );
};

export default PublicEvents;
